import {ClubSubscriptionTier, SubscriptionPlanType} from "@/constants";
import {featuresForTier, RateKey, rateKeyForTier, tierLabel} from "@/pricing/plans";
import {feeRowFor, useFeeSchedule} from "@/pricing/useFeeSchedule";
import {useClubSubscription} from "@/hooks/useClubSubscription";
import PlusTrialPrompt from "@/components/billing/PlusTrialPrompt";
import {useJwtUser} from "@/JWTAuthProvider";
import services from "@/services";
import queryKeys from "@/utilities/queryKeys";
import {Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Skeleton} from "@heroui/react";
import {addToast} from "@heroui/toast";
import {useQuery} from "@tanstack/react-query";
import {Check, Crown, Zap} from "lucide-react";
import {useState} from "react";

export type UpgradeReason = 'players' | 'leagues' | 'tournaments' | 'feature' | 'locked';

interface ClubTierUpgradeModalProps {
    clubUid: string;
    isOpen: boolean;
    onClose: () => void;
    reason?: UpgradeReason;
    featureName?: string;
    onboarding?: boolean;
    /**
     * Offer Starter and Plus only, with Plus recommended — for a feature sold at Plus (WhatsApp
     * Sessions), where Pro would be a distraction even for a legacy club, and Plus is the answer.
     */
    plusFirst?: boolean;
}

const reasonMessages: Record<UpgradeReason, {title: string; description: string}> = {
    players: {
        title: 'Entry Limit Reached',
        description: 'You\'ve reached the maximum number of entries per league for your current plan.',
    },
    leagues: {
        title: 'League Limit Reached',
        description: 'You\'ve reached the maximum number of concurrent leagues for your current plan.',
    },
    tournaments: {
        title: 'Tournament Limit Reached',
        description: 'You\'ve reached the maximum number of concurrent tournaments for your current plan.',
    },
    feature: {
        title: 'Premium Feature',
        description: ' ',
    },
    // Raised by a server-side `feature_locked` 403, so the organizer sees which capability
    // stopped them rather than a bare failure.
    locked: {
        title: 'Not included in your plan',
        description: 'Your plan does not include this yet.',
    },
};

export default function ClubTierUpgradeModal({
    clubUid,
    isOpen,
    onClose,
    reason = 'feature',
    featureName,
    onboarding,
    plusFirst = false,
}: ClubTierUpgradeModalProps) {
    const user = useJwtUser();
    const subscription = useClubSubscription(clubUid);
    const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');

    const pricingPlansQuery = useQuery({
        queryKey: queryKeys.pricing_plans(user?.uid ?? ''),
        queryFn:  () => services.pricing.getPlans(),
        enabled:  isOpen,
    });

    // Per-country processing fees — the real selling point (higher plans pay a lower rate) — from
    // the schedule this organiser's account is charged on: payments v3's all-in fee, or pre-v3.
    const feesQuery = useQuery({
        queryKey: ['pricing-fees'],
        queryFn:  () => services.pricing.getFees(),
        enabled:  isOpen,
    });
    const feeRow = feeRowFor(useFeeSchedule(feesQuery.data, isOpen), user?.country_code, feesQuery.data?.default_country);
    const fmtFee = (n: number) => {
        try {
            return new Intl.NumberFormat(undefined, {style: 'currency', currency: feeRow?.currency ?? 'USD'}).format(n);
        } catch {
            return `${feeRow?.currency ?? 'USD'} ${n.toFixed(2)}`;
        }
    };
    // Under payments v3 Plus and Pro pay the same rate, so a Plus club has no lower rate to upgrade to.
    const lowerRateAbove = !!feeRow && Math.min(feeRow.plan_rates.club, feeRow.plan_rates.pro) < feeRow.plan_rates[rateKeyForTier(subscription.tier)];
    const feeNote = (key: RateKey): string | undefined =>
        feeRow ? `${feeRow.plan_rates[key]}% + ${fmtFee(feeRow.fixed_cents / 100)} per registration` : undefined;

    const message = reasonMessages[reason];
    const currentTier = subscription.tier;
    const isYearly = billingCycle === 'yearly';
    // Which commercial model this club is on decides both what we may sell it and what the plans
    // are CALLED. A legacy club keeps its old names and its old feature bullets; anything else gets
    // the model-2 copy from pricing/plans.ts, the same copy /pricing shows.
    const isLegacy = subscription.isLegacyPricing;

    // Get prices from backend
    const basicMonthly = pricingPlansQuery.data?.[SubscriptionPlanType.BasicMonthly]?.price ?? 0;
    const basicYearly = pricingPlansQuery.data?.[SubscriptionPlanType.BasicYearly]?.price ?? 0;
    const starterMonthly = pricingPlansQuery.data?.[SubscriptionPlanType.StarterMonthly]?.price ?? 0;
    const starterYearly = pricingPlansQuery.data?.[SubscriptionPlanType.StarterYearly]?.price ?? 0;
    const proMonthly = pricingPlansQuery.data?.[SubscriptionPlanType.ProMonthly]?.price ?? 0;
    const proYearly = pricingPlansQuery.data?.[SubscriptionPlanType.ProYearly]?.price ?? 0;
    const currencyCode = pricingPlansQuery.data?.[SubscriptionPlanType.StarterMonthly]?.currency_code ?? 'USD';
    // Plans are in US dollars: "$19", not "USD 19". Any other code still shows its name.
    const money = (n: number | string) => currencyCode === 'USD' ? `$${n}` : `${currencyCode} ${n}`;

    const handleUpgrade = async (plan: number) => {
        try {
            const result = await subscription.checkout({
                plan,
                currencyCode,
                onboarding,
            });
            if (result.redirect_url) {
                window.location.href = result.redirect_url;
            } else {
                addToast({color: 'danger', title: 'Could not start checkout. Please try again.'});
            }
        } catch (error) {
            console.error('Checkout failed:', error);
            addToast({color: 'danger', title: (error as any)?.message || 'Could not start checkout. Please try again.'});
        }
    };

    // Determine which upgrade options to show based on current tier
    // Under the go-live gate (auth/me `golive_gate`) the Basic tier is sold yearly only — the Annual
    // Pass — so its card shows in the yearly view alone. Existing monthly Basic subscribers are
    // above Free and never see this card anyway.
    const annualOnly = !!(user as any)?.golive_gate;
    const showBasicUpgrade = currentTier === ClubSubscriptionTier.Free && (!annualOnly || isYearly);
    const showStarterUpgrade = currentTier < ClubSubscriptionTier.Starter;

    // Pro is only sold to legacy (pricing_model = 1) clubs. On the current model the plans are
    // Free, Starter and Plus — offering Pro here contradicted /pricing, where it no longer exists.
    // `isLegacyPricing` is false while the subscription query is still loading, so the transient
    // state hides the card rather than flashing a plan we do not sell. The real guard is in
    // ClubSubscriptionController::createCheckout; this only stops us asking.
    const showProUpgrade = isLegacy && currentTier < ClubSubscriptionTier.Pro && !plusFirst;

    const basicPlan = isYearly ? SubscriptionPlanType.BasicYearly : SubscriptionPlanType.BasicMonthly;
    const starterPlan = isYearly ? SubscriptionPlanType.StarterYearly : SubscriptionPlanType.StarterMonthly;
    const proPlan = isYearly ? SubscriptionPlanType.ProYearly : SubscriptionPlanType.ProMonthly;

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg" placement={"top"}>
            <ModalContent>
                <ModalHeader className="flex items-center gap-2">
                    <Crown className="w-5 h-5 text-warning" />
                    {message.title}
                </ModalHeader>
                <ModalBody>
                    <p className="text-default-500 mb-4">
                        {message.description}
                        {featureName && ` "${featureName}" requires a higher tier subscription.`}
                    </p>
                    {/* Choosing a plan: the Plus trial is offered here, beside the plans. */}
                    <div className="mb-4"><PlusTrialPrompt clubUid={clubUid} /></div>

                    <div className="flex justify-between items-center mb-4">
                        <div>
                            <p>
                                Current plan: <strong>{tierLabel(currentTier, isLegacy)}</strong>
                            </p>
                            {feeNote(rateKeyForTier(currentTier)) && (
                                <p className="text-xs text-default-500">
                                    Your fee today: {feeNote(rateKeyForTier(currentTier))}{lowerRateAbove && ' — upgrade for a lower rate'}.
                                </p>
                            )}
                        </div>
                        <div className="flex items-center gap-2 bg-default-100 p-1 rounded-lg">
                            <button
                                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                                    billingCycle === 'monthly'
                                        ? 'bg-white shadow-sm text-foreground'
                                        : 'text-default-500 hover:text-foreground'
                                }`}
                                onClick={() => setBillingCycle('monthly')}
                            >
                                Monthly
                            </button>
                            <button
                                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5 ${
                                    billingCycle === 'yearly'
                                        ? 'bg-white shadow-sm text-foreground'
                                        : 'text-default-500 hover:text-foreground'
                                }`}
                                onClick={() => setBillingCycle('yearly')}
                            >
                                Yearly
                                <span className="text-xs bg-success-100 text-success-700 px-1.5 py-0.5 rounded">
                                    -17%
                                </span>
                            </button>
                        </div>
                    </div>

                    <Skeleton isLoaded={pricingPlansQuery.isSuccess} className="rounded-lg">
                        <div className="space-y-4">
                            {showBasicUpgrade && (
                                <UpgradeOption
                                    tier={ClubSubscriptionTier.Basic}
                                    isLegacy={isLegacy}
                                    price={isYearly ? `${money(basicYearly)}/year` : `${money(basicMonthly)}/month`}
                                    monthlyEquivalent={isYearly ? `${money(Math.round(basicYearly / 12))}/month` : undefined}
                                    features={featuresForTier(ClubSubscriptionTier.Basic, isLegacy)}
                                    feeNote={feeNote(rateKeyForTier(ClubSubscriptionTier.Basic))}
                                    recommended={reason === 'players' && !plusFirst}
                                    onSelect={() => handleUpgrade(basicPlan)}
                                    isLoading={subscription.isCheckingOut}
                                />
                            )}

                            {showStarterUpgrade && (
                                <UpgradeOption
                                    tier={ClubSubscriptionTier.Starter}
                                    isLegacy={isLegacy}
                                    price={isYearly ? `${money(starterYearly)}/year` : `${money(starterMonthly)}/month`}
                                    monthlyEquivalent={isYearly ? `${money(Math.round(starterYearly / 12))}/month` : undefined}
                                    features={featuresForTier(ClubSubscriptionTier.Starter, isLegacy)}
                                    feeNote={feeNote(rateKeyForTier(ClubSubscriptionTier.Starter))}
                                    // Plus is the top plan once Pro is off the table, so the
                                    // recommendation that used to sit on Pro lands here instead —
                                    // otherwise a model-2 free club sees nothing highlighted at all.
                                    recommended={plusFirst || (!showProUpgrade && currentTier === ClubSubscriptionTier.Free && reason !== 'players')}
                                    onSelect={() => handleUpgrade(starterPlan)}
                                    isLoading={subscription.isCheckingOut}
                                />
                            )}

                            {showProUpgrade && (
                                <UpgradeOption
                                    tier={ClubSubscriptionTier.Pro}
                                    isLegacy={true}
                                    price={isYearly ? `${money(proYearly)}/year` : `${money(proMonthly)}/month`}
                                    monthlyEquivalent={isYearly ? `${money(Math.round(proYearly / 12))}/month` : undefined}
                                    features={featuresForTier(ClubSubscriptionTier.Pro, true)}
                                    feeNote={feeNote(rateKeyForTier(ClubSubscriptionTier.Pro))}
                                    recommended={currentTier === ClubSubscriptionTier.Free && reason !== 'players'}
                                    onSelect={() => handleUpgrade(proPlan)}
                                    isLoading={subscription.isCheckingOut}
                                />
                            )}
                        </div>
                    </Skeleton>

                    <p className="text-xs text-default-400 mt-4 text-center">
                        Need enterprise features?{' '}
                        <a href="mailto:support@scorepal.co" className="text-primary">
                            Contact us
                        </a>{' '}
                        for custom pricing.
                    </p>
                </ModalBody>
                <ModalFooter>
                    <Button variant="flat" onPress={onClose}>
                        Maybe Later
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

function UpgradeOption({
    tier,
    isLegacy,
    price,
    monthlyEquivalent,
    features,
    feeNote,
    recommended,
    onSelect,
    isLoading,
}: {
    tier: number;
    isLegacy: boolean;
    price: string;
    monthlyEquivalent?: string;
    features: string[];
    feeNote?: string;
    recommended?: boolean;
    onSelect: () => void;
    isLoading: boolean;
}) {
    const label = tierLabel(tier, isLegacy);

    return (
        <div className={`p-4 rounded-lg border ${recommended ? 'border-primary bg-primary/5' : 'border-default-200'}`}>
            <div className="flex justify-between items-start mb-2">
                <div>
                    <h4 className="font-semibold flex items-center gap-2">
                        {label}
                        {recommended && (
                            <span className="text-xs bg-primary text-white px-2 py-0.5 rounded">
                                Recommended
                            </span>
                        )}
                    </h4>
                    <p className="text-sm text-default-500">{price}</p>
                    {monthlyEquivalent && (
                        <p className="text-xs text-default-400">{monthlyEquivalent} billed annually</p>
                    )}
                    {feeNote && (
                        <p className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-success-50 px-2 py-0.5 text-xs font-semibold text-success-700">
                            {feeNote}
                        </p>
                    )}
                </div>
                <Button
                    size="sm"
                    color={recommended ? 'primary' : 'default'}
                    startContent={<Zap className="w-4 h-4" />}
                    onPress={onSelect}
                    isLoading={isLoading}
                >
                    Upgrade
                </Button>
            </div>
            <ul className="text-sm space-y-1">
                {features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-default-600">
                        <Check className="w-3 h-3 text-success" />
                        {feature}
                    </li>
                ))}
            </ul>
        </div>
    );
}
